// What mark a visual is drawn with, chosen by what its data is, and the kit
// chart that draws it. Shared by the Dashboard (capped, two cards per kind)
// and Insight (uncapped). A time series is only ever a line or an area; a
// ranking is bars or tiles; a part to whole a donut; a process a funnel;
// ordered bins columns. Never a numbered key: every chart prints its names.
import type { ReactNode } from "react";
import { BarList, ColumnChart2, DeviationList, DonutChart, FunnelChart2, HeatTiles, StepChart, TimeChart, VizFigure, WaffleChart, groupParts } from "@dashboard-intelligent/ui";
import type { Visual } from "@dashboard-intelligent/api/src/modules/industry/types";
import { formatMetric, metricFormatter } from "../../format";
import { isBucketAxis, isPeriodAxis, longestWord, nonNegative } from "./metric";

export type Mark = "area" | "line" | "step" | "bars" | "columns" | "funnel" | "donut" | "waffle" | "heat" | "deviation";

/** The kinds the Dashboard caps at two cards each. Every rectangle on a baseline (bars, columns) is one kind. */
export type MarkKind = "area" | "line" | "step" | "bars" | "funnel" | "donut" | "waffle" | "heat" | "deviation" | "scatter";

export function markKind(mark: Mark | "scatter"): MarkKind {
  return mark === "columns" ? "bars" : mark;
}

/** A column chart carries this many bins at most, and words of this many characters at most. */
const COLUMNS_MAX = 10;
const COLUMN_WORD_MAX = 10;

/** Every step holds at most the one before it: the counts only fall. */
export function falls(data: { value: number }[]): boolean {
  return data.every((d, i) => i === 0 || d.value <= data[i - 1]!.value);
}

/** Tiles read only where their tones differ: the largest at least half again the smallest. */
function spread(data: { value: number }[]): boolean {
  const v = data.map((d) => d.value).filter((n) => n > 0);
  return v.length > 0 && Math.max(...v) >= 1.5 * Math.min(...v);
}

/** Values that are averages, rates, prices or indices: a share of their sum means nothing. */
export function isAverage(visual: Visual): boolean {
  if (visual.format === "percent" || visual.format === "decimal" || visual.format === "score" || visual.format === "minutes" || visual.format === "hours" || visual.format === "days") return true;
  const words = `${visual.title} ${"valueLabel" in visual ? visual.valueLabel : ""}`;
  return /rata-rata|rerata|average|harga|per malam|per tiket|indeks|index|rate|margin|ROAS|ROI|ARPU|ADR|RevPAR|per nasabah|per unit|per gerai|per pelanggan|per member/i.test(words);
}

/** Revenue, costs and profit lines in one list: a statement, never parts of one whole. */
function isStatement(visual: Visual): boolean {
  return /P&L|laba rugi|profit and loss/i.test(visual.title);
}

/** The honest marks for a visual, in order of preference. Bars come last wherever another form says it as well. */
export function markOptions(visual: Visual): Mark[] {
  const positive = nonNegative(visual);
  switch (visual.kind) {
    case "trend":
      return visual.series.length === 1 ? ["area", "line"] : ["line"];
    case "funnel":
      return positive && falls(visual.data) ? ["funnel", "step"] : ["step"];
    case "part":
      if (!positive || visual.data.length < 2 || isStatement(visual)) return ["bars"];
      return [...(visual.data.length <= 6 ? (["donut"] as Mark[]) : []), ...(spread(visual.data) ? (["waffle"] as Mark[]) : []), "bars"];
    case "column":
      if (isPeriodAxis(visual.data)) return positive ? ["area", "line", "columns"] : ["line"];
      // Binned data (a histogram, weekdays) keeps its order: columns, the one honest bar exception.
      if (positive && isBucketAxis(visual.data) && visual.data.length <= COLUMNS_MAX && longestWord(visual.data) <= COLUMN_WORD_MAX) return ["columns", "bars"];
      if (/pareto/i.test(visual.title)) return ["bars"];
      return positive ? (isAverage(visual) ? ["deviation", "bars"] : [...(spread(visual.data) ? (["heat"] as Mark[]) : []), "deviation", "bars"]) : ["bars"];
    case "ranked": {
      if (!positive || isStatement(visual) || /pareto/i.test(visual.title)) return ["bars"];
      if (isAverage(visual)) return [...(spread(visual.data) ? (["heat"] as Mark[]) : []), "deviation", "bars"];
      return [...(spread(visual.data) ? (["waffle", "heat"] as Mark[]) : []), "deviation", "bars"];
    }
  }
}

/** A label in display case: ALL CAPS and all-lowercase names from the data read in sentence or title case. */
export function displayLabel(label: string): string {
  const letters = label.replace(/[^A-Za-z]/g, "");
  if (letters.length >= 4 && letters === letters.toUpperCase()) {
    return label.toLowerCase().replace(/(^|[\s/(-])([a-z])/g, (_m, p: string, c: string) => p + c.toUpperCase());
  }
  if (/^[a-z]/.test(label) && !/[A-Z]/.test(label)) return label.charAt(0).toUpperCase() + label.slice(1);
  return label;
}

/** A title that repeats its last phrase ("per bulan per bulan") reads it once. */
export function tidyTitle(title: string): string {
  return title.replace(/((?:\s+per\s+[^\s]+)+)\1/gi, "$1").replace(/\b(per \w+) \1\b/gi, "$1");
}

/** The visual with every category name in display case. */
export function displayVisual(raw: Visual): Visual {
  const visual = { ...raw, title: tidyTitle(raw.title) } as Visual;
  if (visual.kind === "trend") return { ...visual, series: visual.series.map((s) => ({ ...s, label: displayLabel(s.label) })) };
  return { ...visual, data: visual.data.map((d) => ({ ...d, label: displayLabel(d.label) })) } as Visual;
}

/** A categorical visual as a single-series time chart's rows. */
function asSeries(visual: Extract<Visual, { kind: "column" }>) {
  return {
    data: visual.data.map((d) => ({ label: d.label, value: d.value })),
    series: [{ key: "value", label: visual.valueLabel }],
  };
}

/** Lowercase the first letter of a label unless it opens an acronym ("Channel" to "channel", "NPL" stays). */
function lowerFirst(text: string): string {
  return text.length > 1 && text[1] === text[1]!.toUpperCase() && /[A-Z]/.test(text[1]!) ? text : text.charAt(0).toLowerCase() + text.slice(1);
}

/** The one line of context under a chart card's title: how many marks of what, and the measure when the title does not name it. */
export function visualContext(visual: Visual): string {
  if (visual.kind === "trend") {
    const first = visual.data[0]?.label;
    const last = visual.data[visual.data.length - 1]?.label;
    const span = first && last ? `${first} sampai ${last}` : "";
    return visual.series.length > 1 ? `${visual.series.length} seri, ${span}` : span;
  }
  const named = visual.title.toLowerCase().includes(visual.valueLabel.toLowerCase());
  const measure = named ? "" : `, ${lowerFirst(visual.valueLabel)}`;
  if (visual.kind === "funnel") return `${visual.data.length} tahap${measure}`;
  return `${visual.data.length} ${lowerFirst(visual.categoryLabel)}${measure}`;
}

const pct = new Intl.NumberFormat("id-ID", { maximumFractionDigits: 1 });

/** The takeaway figure of a chart: the one number a reader carries away. */
export function visualFigure(raw: Visual, mark: Mark): ReactNode {
  const visual = displayVisual(raw);
  const f = (n: number) => formatMetric(n, visual.format);
  if (visual.kind === "trend") {
    const lead = visual.series[0];
    const last = visual.data[visual.data.length - 1];
    if (!lead || !last) return null;
    return <VizFigure value={f(Number(last[lead.key]) || 0)} label={visual.series.length > 1 ? `${lead.label}, ${last.label}` : `Terakhir, ${last.label}`} />;
  }
  const data = visual.data;
  if (data.length === 0) return null;
  if (visual.kind === "funnel" && !falls(data)) {
    const peak = data.reduce((best, d) => (d.value > best.value ? d : best), data[0]!);
    const end = data[data.length - 1]!;
    return <VizFigure value={f(end.value)} label={`di ${end.label}, tahap terakhir; terbanyak ${f(peak.value)} di ${peak.label}`} />;
  }
  if (visual.kind === "funnel") {
    const first = data[0]!;
    const end = data[data.length - 1]!;
    if (first.value <= 0) return null;
    return <VizFigure value={`${pct.format((end.value / first.value) * 100)}%`} label={`sampai ${end.label}, dari ${first.label}`} />;
  }
  if (mark === "area" || mark === "line") {
    const last = data[data.length - 1]!;
    return <VizFigure value={f(last.value)} label={`Terakhir, ${last.label}`} />;
  }
  if (mark === "donut" || mark === "waffle") {
    const parts = groupParts(data);
    const total = parts.reduce((s, d) => s + d.value, 0);
    const top = parts[0];
    if (!top || total <= 0) return null;
    return <VizFigure value={`${pct.format((top.value / total) * 100)}%`} label={`porsi terbesar, ${top.label}`} />;
  }
  const top = data.reduce((best, d) => (d.value > best.value ? d : best), data[0]!);
  return <VizFigure value={f(top.value)} label={mark === "columns" ? `terbanyak, ${top.label}` : `tertinggi, ${top.label}`} />;
}

export interface VisualChartProps {
  visual: Visual;
  mark: Mark;
  /** A level target drawn on a time chart. */
  target?: { value: number; label: string };
  /** Rows a ranking draws at most. */
  limit?: number;
}

/** One visual drawn with its mark by the kit. */
export function VisualChart({ visual: raw, mark, target, limit }: VisualChartProps) {
  const visual = displayVisual(raw);
  const format = metricFormatter(visual.format);
  const label = visual.title;
  if (visual.kind === "trend") {
    return <TimeChart label={label} data={visual.data} series={visual.series} format={format} target={target} categoryLabel={visual.categoryLabel} />;
  }
  if ((mark === "area" || mark === "line") && visual.kind === "column") {
    const { data, series } = asSeries(visual);
    return <TimeChart label={label} data={data} series={series} format={format} target={target} categoryLabel={visual.categoryLabel} />;
  }
  const categoryLabel = visual.kind === "funnel" ? "Tahap" : visual.categoryLabel;
  const valueLabel = visual.valueLabel;
  switch (mark) {
    case "funnel":
      return <FunnelChart2 label={label} data={visual.data} format={format} valueLabel={valueLabel} />;
    case "step":
      return <StepChart label={label} data={visual.data} format={format} valueLabel={valueLabel} />;
    case "deviation":
      // Averages, rates and durations: each category's difference from their average, never a dot on a zoomed axis.
      return <DeviationList label={label} data={visual.data} format={format} categoryLabel={categoryLabel} valueLabel={valueLabel} limit={limit} />;
    case "waffle":
      return <WaffleChart label={label} data={visual.data} format={format} categoryLabel={categoryLabel} valueLabel={valueLabel} unit="share" />;
    case "donut":
      return <DonutChart label={label} data={visual.data} format={format} categoryLabel={categoryLabel} valueLabel={valueLabel} />;
    case "columns":
      return <ColumnChart2 label={label} data={visual.data} format={format} categoryLabel={categoryLabel} valueLabel={valueLabel} />;
    case "heat":
      return <HeatTiles label={label} data={visual.data} format={format} categoryLabel={categoryLabel} valueLabel={valueLabel} limit={limit} />;
    default: {
      const ordered = visual.kind === "funnel" || (visual.kind === "column" && isBucketAxis(visual.data));
      const percentScale = visual.format === "percent" && visual.data.every((d) => d.value >= 0 && d.value <= 100);
      return (
        <BarList
          label={label}
          data={visual.data}
          format={format}
          categoryLabel={categoryLabel}
          valueLabel={valueLabel}
          sort={!ordered}
          max={percentScale ? 100 : undefined}
          limit={limit}
        />
      );
    }
  }
}
